import React, { FC, ReactNode, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { number, object, string } from 'yup';

import { StackedIconCTA } from 'Src/components/buttons/StackedIconCTA';
import Copy from 'Src/components/Copy';
import { Flex } from 'Src/components/layout/Flex';
import { CardReadSuccessOptions, useCardReader } from 'Src/feature/card-reader/useCardReader';
import { Check } from 'Src/feature/check/models';
import { useCheck } from 'Src/feature/check/useCheck';
import { useLogger } from 'Src/feature/context-logger/useLogger';
import { getTypedFormComponents } from 'Src/feature/form/getTypedFormComponents';
import { useForm } from 'Src/feature/form/useForm';
import { CreateGiftCardRequest, GiftCardBalance } from 'Src/feature/gift-card/giftCardService';
import { useGiftCard } from 'Src/feature/gift-card/useGiftCard';
import { Modal } from 'Src/feature/modal/Modal';
import { ModalAction } from 'Src/feature/modal/ModalAction';
import { ModalActions } from 'Src/feature/modal/ModalActions';
import { ModalContent } from 'Src/feature/modal/ModalContent';
import { ModalHeader } from 'Src/feature/modal/ModalHeader';
import { ModalSubTitle } from 'Src/feature/modal/ModalSubTitle';
import { ModalTitle } from 'Src/feature/modal/ModalTitle';
import { useModalControl } from 'Src/feature/modal/useModalControl';
import { PaymentIcon } from 'Src/feature/payment/PaymentIcon';
import { PaymentMethodModal } from 'Src/feature/payment/PaymentMethodModal';
import { usePaymentMethod } from 'Src/feature/payment/usePaymentMethod';
import { useToast } from 'Src/feature/toasts/useToast';
import { Uuid } from 'Src/models/Uuid';
import { sortByLabel } from 'Src/utils/sort';

import styles from './GiftCardAddToCheckModal.module.scss';

const sectionId = 'GiftCardAddToCheckModal';

const { Form: AmtForm, Field: AmtField } = getTypedFormComponents<{ addFundsAmt: number }>();
const { Form: AcctForm, Field: AcctField } = getTypedFormComponents<{ account: string }>();

interface GiftCardAddToCheckModalProps {
  check: Check;
}

const GiftCardAddToCheckModal: FC<GiftCardAddToCheckModalProps> = ({ check }) => {
  const { t } = useTranslation();
  const log = useLogger().getSubLogger({ name: sectionId });
  const toast = useToast();
  const cardReader = useCardReader();

  const giftCardPaymentMethods = usePaymentMethod.query.giftCardMethods();
  const giftCardPaymentMethodOptions = useMemo(() => (
    giftCardPaymentMethods.data?.map((pm) => ({ value: pm.id as number, label: pm.name })).sort(sortByLabel) ?? []
  ), [giftCardPaymentMethods.data]);

  const giftCardBalance = useGiftCard.mutate.balance();
  const addGiftCard = useCheck.mutate.addGiftCard();

  const [gcBal, setGcBal] = useState<GiftCardBalance | undefined>(undefined);
  const [gcAccountNumber, setGcAccountNumber] = useState('');
  const [isAddFunds, setIsAddFunds] = useState(false);
  const [payMethodId, setPayMethodId] = useState<number | undefined>(undefined);

  const amtForm = useForm<{ addFundsAmt: number }>({
    defaultValues: {
      addFundsAmt: 0,
    },
    schema: object().shape({
      addFundsAmt: number().label(t('labels.amount')).required().moreThan(0),
    }),
  });

  const acctNumberForm = useForm<{ account: string }>({
    schema: object().shape({
      account: string().label(t('giftCard.labels.accountNumber')).required(),
    }),
  });

  const modal = useModalControl({
    onOpen: () => {
      initReader();
    },
    onClose: () => {
      void cardReader.dispose();
    },
  });

  const giftCardMethodModal = {
    manualBalance: useModalControl(),
    swipeBalance: useModalControl(),
    addToCheck: useModalControl(),
  };

  const initReader = () => {
    void cardReader.init({
      mode: 'msr',
      amount: 0,
      onSuccess: (read) => {
        if (!read || !read.pax) {
          log.error('reInitCardReader()', 'no pax data received');
          return false;
        }
        onCardReaderSuccess(read);
      },
      onTimeout: () => true,
    });
  };

  const readRef = useRef(undefined as undefined | CardReadSuccessOptions);
  const lookupBalance = (read: CardReadSuccessOptions, paymentMethodId: number) => {
    setPayMethodId(paymentMethodId);
    giftCardMethodModal.swipeBalance.close();
    const account = read.pax.giftcardData.number;
    giftCardBalance.mutate({ giftCardBalanceRequest: { account, paymentMethodId } }, {
      onError: (err) => {
        toast.show.serviceError(err, { id: `${sectionId}.onManualLookup`, title: t('giftCard.errors.balance') });
        read.reInit();
        readRef.current = undefined;
      },
      onSuccess: (resp) => {
        setGcBal(resp);
        setGcAccountNumber(account);
        readRef.current = undefined;
      },
    });
  };

  const onCardReaderSuccess = (read: CardReadSuccessOptions) => {
    // stash read in case we have a secondary flow (pick pay method) kick off
    if (!readRef.current) {
      readRef.current = read;
    }
    if (giftCardPaymentMethodOptions.length < 1) {
      toast.show.error({
        id: `${sectionId}.onCardReaderSuccess`,
        title: t('giftCard.labels.lookup'),
        message: t('giftCard.labels.noGiftCardPaymentMethods'),
      });
      return;
    }
    const payMethodWithMatchingMerchId = (giftCardPaymentMethods.data ?? []).find((pm) => pm.docsOnlyFieldsGiftCard?.merchantId === read.pax.giftcardData.merchId);
    if (giftCardPaymentMethodOptions.length > 1 && !payMethodWithMatchingMerchId) {
      giftCardMethodModal.swipeBalance.open();
      return;
    }
    lookupBalance(read, payMethodWithMatchingMerchId?.id ?? giftCardPaymentMethodOptions[0].value);
  };

  const onManualLookup = async (paymentMethodId?: number) => {
    const { isValid, values } = await acctNumberForm.action.submit();
    if (!isValid) return;
    if (giftCardPaymentMethodOptions.length > 1 && !paymentMethodId) {
      giftCardMethodModal.manualBalance.open();
      return;
    }
    if (paymentMethodId) setPayMethodId(paymentMethodId);
    giftCardMethodModal.manualBalance.close();
    const account = values.account.toString();
    giftCardBalance.mutate({ giftCardBalanceRequest: { account, paymentMethodId: paymentMethodId ?? giftCardPaymentMethodOptions[0].value } }, {
      onError: (err) => toast.show.serviceError(err, { id: `${sectionId}.onManualLookup`, title: t('giftCard.errors.balance') }),
      onSuccess: (resp) => {
        setGcBal(resp);
        setGcAccountNumber(account);
      },
    });
  };

  const handleClose = () => {
    modal.close();
    setGcBal(undefined);
    setIsAddFunds(false);
    setGcAccountNumber('');
    setPayMethodId(undefined);
    amtForm.action.reset();
    acctNumberForm.action.reset();
  };

  const addGiftCardToCheck = async (paymentMethodId?: number) => {
    try {
      await addGiftCard.mutateAsync({
        checkId: check.id,
        giftCard: {
          account: gcAccountNumber,
          amount: parseFloat(amtForm.action.getValues().addFundsAmt.toString()),
          cardEntryMethod: 'CardSwipe' as CreateGiftCardRequest['cardEntryMethod'],
          id: Uuid.new(),
          isReload: gcBal?.issued ?? false, // if the gift card has already been issued, then this is a reload
          merchantId: gcBal?.merchantId ?? '',
          paymentMethodId: paymentMethodId ?? giftCardPaymentMethodOptions[0].value,
        }
      });
      handleClose();
    } catch (err) {
      toast.show.serviceError(err, { id: `${sectionId}.addGiftCardToCheck`, title: t('giftCard.errors.addToCheck') });
    }
  };


  let modalHeader: ReactNode;
  if (gcBal === undefined) {
    modalHeader = (
      <ModalHeader>
        <ModalTitle copy={t('giftCard.label')} />
      </ModalHeader>
    );
  } else if (!gcBal.issued) {
    modalHeader = (
      <ModalHeader>
        <ModalTitle copy={t('giftCard.labels.activate')} />
        <ModalSubTitle copy={t('giftCard.labels.neverActivated')} />
      </ModalHeader>
    );
  } else if (gcBal.issued && !isAddFunds) {
    modalHeader =(
      <ModalHeader>
        <ModalTitle copy={t('giftCard.labels.balance')} />
      </ModalHeader>
    );
  } else if (isAddFunds) modalHeader = (
    <ModalHeader>
      <ModalTitle copy={t('giftCard.labels.addFundsToGiftCard')} />
      <ModalSubTitle copy={t('giftCard.labels.currentBalance', { balance: gcBal.balance.toFixed(2) })} />
    </ModalHeader>
  );

  let content: ReactNode;
  if (gcBal === undefined) {
    content = (
      <div className={styles['content']}>
        <Flex horizontal alignItems="center" gap="sm">
          <PaymentIcon paymentType="GiftCard" size="md" />
          <Copy variant="body" value={t('giftCard.instruction')} />
        </Flex>
        <AcctForm usedForm={acctNumberForm} omitChangeTracking>
          <AcctField type="numberInput" name="account" />
        </AcctForm>
      </div>
    );
  } else if (gcBal.issued && !isAddFunds) {
    content = (
      <div className={styles['content']}>
        <Copy type="currency" variant="h4" value={gcBal.balance} />
      </div>
    );
  } else if (!gcBal.issued || isAddFunds) {
    content = (
      <div className={styles['content']}>
        <AmtForm usedForm={amtForm} omitChangeTracking>
          <AmtField
            type="currencyInput"
            name="addFundsAmt"
            className={styles['currency-input']}
          />
        </AmtForm>
      </div>
    );
  }

  let actions: ReactNode;
  if (gcBal === undefined) {
    actions = (
      <ModalActions>
        <ModalAction
          data-testid={`${sectionId}-cancel-btn`}
          color="secondary"
          label={t('labels.cancel')}
          onClick={handleClose}
        />
        <ModalAction
          color="primary"
          label={t('giftCard.labels.lookup')}
          onClick={() => onManualLookup()}
          disabled={!acctNumberForm.state.isValid}
        />
      </ModalActions>
    );
  } else if (!gcBal.issued || isAddFunds) {
    actions = (
      <ModalActions>
        <ModalAction
          data-testid={`${sectionId}-cancel-btn`}
          color="secondary"
          label={t('labels.cancel')}
          onClick={handleClose}
        />
        <ModalAction
          data-testid={`${sectionId}-add-to-check-btn`}
          color="primary"
          label={t('giftCard.labels.addToCheck')}
          disabled={!amtForm.state.isValid}
          onClick={() => addGiftCardToCheck(payMethodId)}
        />
      </ModalActions>
    );
  } else if (gcBal.issued && !isAddFunds) {
    actions = (
      <ModalActions>
        <ModalAction
          data-testid={`${sectionId}-cancel-btn`}
          color="secondary"
          label={t('labels.cancel')}
          onClick={handleClose}
        />
        <ModalAction
          data-testid={`${sectionId}-add-to-check-btn`}
          color="primary"
          label={t('giftCard.labels.addFunds')}
          onClick={() => setIsAddFunds(true)}
        />
      </ModalActions>
    );
  }

  return (
    <>
      <StackedIconCTA
        size="sm"
        padding="sm"
        icon="redeem"
        label={t('labels.giftCard')}
        onClick={modal.open}
        disabled={check.status === 'Scheduled'}
      />
      <Modal
        data-testid={sectionId}
        open={modal.isOpen}
        onBackDropClick={handleClose}
        loading={giftCardBalance.isPending}
      >
        {modalHeader}
        <ModalContent>
          {content}
        </ModalContent>
        {actions}
      </Modal>
      <PaymentMethodModal
        open={giftCardMethodModal.manualBalance.isOpen}
        title={t('payment.action.selectGCMethod')}
        paymentMethodOptions={giftCardPaymentMethodOptions}
        onOptionClick={(option: { value: number }) => void onManualLookup(option.value)}
        onClose={giftCardMethodModal.manualBalance.close}
      />
      <PaymentMethodModal
        open={giftCardMethodModal.swipeBalance.isOpen}
        title={t('payment.action.selectGCMethod')}
        paymentMethodOptions={giftCardPaymentMethodOptions}
        onOptionClick={(option: { value: number }) => {
          if (readRef.current) lookupBalance(readRef.current, option.value);
        }}
        onClose={() => {
          giftCardMethodModal.swipeBalance.close();
          initReader();
        }}
      />
    </>
  );
};

export { GiftCardAddToCheckModal };
